/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { existsNestedPath, getArrayedNestedPath } from "@/lib/objectPath-helper";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { PageFilterOperationEnum } from "./page-filter-operation.enum";
import { PageFilterTypes } from "./page-filter-types.enum";

/**
 * Applies a list of page filters to a list of entities and returns the matching subset.
 *
 * This is the exact filter-matching predicate that used to live inline inside
 * `BaseStore.filtered` (`store/base.store.ts`), extracted into a standalone,
 * side-effect-light pure function so it can:
 * - run against an arbitrary filter list instead of only `this.filters` (used by
 *   `FilteredPager` to scope filters per-consumer instead of sharing the store singleton), and
 * - be unit-tested directly (entities + filters in, filtered array out) instead of
 *   only indirectly through a Vuex module instance.
 *
 * An entity is kept when it matches every filter (logical AND). A single filter matches
 * when at least one value found at its `key` path satisfies the filter operation.
 *
 * @param entities the entities to filter
 * @param filters the filters to apply (combine your visible and hidden filters before calling)
 * @returns the entities that match all filters. When `filters` is empty the input array is returned as-is.
 */
export function applyPageFilters<T>(entities: T[], filters: IPageFilterElement[]): T[] {
  if (!filters.length) {
    return entities;
  }

  return entities.filter((element) => {
    return filters.every((filter) => {
      const filterType = filter.type as PageFilterTypes;
      const filterPath = filter.key;
      const filterSpecifier = filter.specifier;

      let values: (string | boolean | number)[] = [];
      if (
        [
          PageFilterTypes.CUSTOM_FIELD_VALUE,
          PageFilterTypes.CUSTOM_FIELD_NUMBER_VALUE,
          PageFilterTypes.CUSTOM_FIELD_DATE_VALUE
        ].includes(filterType) &&
        filterSpecifier
      ) {
        /**
         * Special case for custom field filtering.
         * Look up the custom field. It is nested "one level above value" in an element of the array there.
         */
        const pathBits = filterPath.split(".");
        const pathEnd = pathBits.pop();
        const reducedPath = pathBits.join(".");
        const valuesAtReducedPath = getArrayedNestedPath(element, reducedPath);
        const customField = valuesAtReducedPath.find((f) => JSON.stringify(f).includes(filterSpecifier));

        if (!customField || !pathEnd) {
          return false;
        }

        // get values from custom field
        values = getArrayedNestedPath(customField, pathEnd);
      } else {
        values = getArrayedNestedPath(element, filterPath);
      }

      let filterValue: string | boolean = filter.value;
      if ([PageFilterTypes.CUSTOM_FIELD_DATE_VALUE, PageFilterTypes.DATE].includes(filterType)) {
        filterValue = new Date(filterValue).toISOString().split("T")[0];
        values = values.map((v) => new Date(v as string).toISOString().split("T")[0]);
      }

      if ([PageFilterTypes.BOOLEAN].includes(filterType)) {
        if (filterValue === "true") filterValue = true;
        if (filterValue === "false") filterValue = false;
      }

      // Default to EQUAL WITHOUT mutating the caller's filter object. This helper is now
      // shared by `BaseStore.filtered` and every `FilteredPager`, and the same filter
      // instances may be reused across consumers or held in Vuex state, so a local default
      // keeps it referentially transparent and free of surprising side effects.
      const operation = filter.operation || PageFilterOperationEnum.EQUAL;

      if (operation === PageFilterOperationEnum.EXISTS) {
        const exists = existsNestedPath(element, filterPath);
        return filterValue === "true" ? exists : !exists;
      }

      return values.some((v) => {
        const objectValue = v;

        if (operation === "$eq") {
          // use weak comparison to allow for implicit type conversion (e.g. to compare "7" with 7)
          return objectValue == filterValue;
        } else if (operation === "$ne") {
          // use weak comparison
          return objectValue != filterValue;
        } else if (operation === "$gt") {
          return objectValue > filterValue;
        } else if (operation === "$lt") {
          return objectValue < filterValue;
        } else if (operation === "$gte") {
          return objectValue >= filterValue;
        } else if (operation === "$lte") {
          return objectValue <= filterValue;
        } else {
          return false;
        }
      });
    });
  });
}
